import {
  getRequiredDirectReferralsForLevel,
  getTeamAfpWithdrawalRules,
  withdrawalUsesUltraMemberQualification,
} from "../../config/autoFillProgramConfig";
import { ACTIVE_DIRECT_REFERRAL_POLICY } from "../../config/autoFillLevelQualificationConfig";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { computeCappedTeamScore } from "./autoFillTeamScoreCore";

/**
 * Active direct = referred user has at least one active Auto Fill program.
 * Used for withdrawal rules (and display), not for level-up.
 */
export async function countQualifiedDirectReferrals(referrerUserDocId) {
  const refSnap = await getDocs(
    query(
      collection(db, "referrals"),
      where("referrerId", "==", referrerUserDocId)
    )
  );
  if (refSnap.empty) return 0;

  let count = 0;
  for (const refDoc of refSnap.docs) {
    const referredUid = refDoc.data().referredUserId;
    if (!referredUid) continue;
    const progSnap = await getDocs(
      query(
        collection(db, "autoFillPrograms"),
        where("userId", "==", referredUid),
        where("status", "==", "active")
      )
    );
    if (!progSnap.empty) count += 1;
  }
  return count;
}

/** Direct referrals who purchased the Ultra / membership activation package. */
export async function countQualifiedUltraDirectReferrals(referrerUserDocId) {
  const refSnap = await getDocs(
    query(
      collection(db, "referrals"),
      where("referrerId", "==", referrerUserDocId)
    )
  );
  if (refSnap.empty) return 0;

  let count = 0;
  for (const refDoc of refSnap.docs) {
    const referredUid = refDoc.data().referredUserId;
    if (!referredUid) continue;
    const userSnap = await getDocs(
      query(collection(db, "users"), where("uid", "==", referredUid))
    );
    if (userSnap.empty) continue;
    if (userSnap.docs[0].data().hasPurchasedPackage === true) count += 1;
  }
  return count;
}

export async function countQualifiedDirectReferralsForWithdrawal(
  referrerUserDocId,
  programLevel
) {
  const L = Number(programLevel);
  /** Withdrawals 6–15: direct referrals must be Ultra members (activation purchased). */
  if (L >= 6 || withdrawalUsesUltraMemberQualification(L)) {
    return countQualifiedUltraDirectReferrals(referrerUserDocId);
  }
  return countQualifiedDirectReferrals(referrerUserDocId);
}

/**
 * Team score for L7+ withdrawal: members at configured depth only, capped per L1 leg (50%).
 */
export async function countQualifiedTeamAfpMembers(
  referrerUserDocId,
  referrerUid,
  programLevel,
  options = {}
) {
  const rules = options.teamRules ?? getTeamAfpWithdrawalRules(programLevel);
  if (!rules) return 0;
  const needsAfp = rules.memberKind === "active_afp";
  const [usersSnap, refsSnap, progSnap] = await Promise.all([
    getDocs(collection(db, "users")),
    getDocs(collection(db, "referrals")),
    needsAfp
      ? getDocs(
          query(
            collection(db, "autoFillPrograms"),
            where("status", "==", "active")
          )
        )
      : Promise.resolve(null),
  ]);

  const uidToDocId = new Map();
  const ultraUids = new Set();
  usersSnap.docs.forEach((d) => {
    const u = d.data();
    if (u.uid) uidToDocId.set(u.uid, d.id);
    if (u.uid && u.hasPurchasedPackage === true) ultraUids.add(u.uid);
  });

  const qualifiedUids =
    rules.memberKind === "ultra"
      ? ultraUids
      : new Set(
          (progSnap?.docs || [])
            .map((d) => d.data().userId)
            .filter(Boolean)
        );

  const childrenByParentKey = new Map();
  refsSnap.docs.forEach((d) => {
    const r = d.data();
    const parent = r.referrerId;
    const child = r.referredUserId;
    if (!parent || !child) return;
    if (!childrenByParentKey.has(parent)) childrenByParentKey.set(parent, []);
    childrenByParentKey.get(parent).push(child);
  });

  return computeCappedTeamScore({
    referrerUid,
    referrerUserDocId,
    rules,
    childrenByParentKey,
    uidToDocId,
    qualifiedUids,
  });
}

/** Level-up has no T&C / wallet / referral gates — matrix + upgrade pool only. */
export async function evaluateLevelQualification(
  _userProfile,
  _userDocId,
  targetLevel
) {
  const level = Number(targetLevel);
  return {
    qualified: true,
    status: "QUALIFIED",
    targetLevel: level,
    reasons: [],
    gates: {},
    directReferrals: {
      required: getRequiredDirectReferralsForLevel(level),
      current: 0,
      met: true,
      policy: ACTIVE_DIRECT_REFERRAL_POLICY,
    },
  };
}
